/**
 * Utility functions for heatmap feature
 */

export type ScreenshotVersion = {
  mobile_screenshot_url: string | null
  desktop_screenshot_url: string | null
  mobile_screenshot_at: string | null
  desktop_screenshot_at: string | null
  captured_at: string
  version_index: number
}

export type ScrollDepthBucket = {
  startPercent: number
  endPercent: number
  sessionsReached: number
  reachPercentage: number
  dropOffPercentage: number
}

export type ScrollDepthSummary = {
  totalSessions: number
  averageScroll: number
  buckets: ScrollDepthBucket[]
} | null

type ScrollDepthSummaryNonNull = {
  totalSessions: number
  averageScroll: number
  buckets: ScrollDepthBucket[]
}

export const SCROLL_MARKER_STEPS = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0]

/**
 * Convert value to number with fallback
 */
function toNumber(value: any, fallback = 0): number {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(num) ? num : fallback
}

/**
 * Convert value to fixed decimal number
 */
function toFixedNumber(value: any, decimals = 2, fallback = 0): number {
  const num = toNumber(value, fallback)
  return Number(num.toFixed(decimals))
}

/**
 * Clamp percentage value between 0 and 100
 */
function clampPercentage(value: number): number {
  return Math.max(0, Math.min(100, value))
}

/**
 * Linear interpolation
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/**
 * Get color for scroll depth bucket based on index
 */
export function getBucketColor(index: number, total: number): string {
  const start = [239, 68, 68] // red-500
  const end = [59, 130, 246] // blue-500
  const safeTotal = Math.max(total, 1)
  const t = safeTotal === 1 ? 0 : index / (safeTotal - 1)
  const r = Math.round(lerp(start[0], end[0], t))
  const g = Math.round(lerp(start[1], end[1], t))
  const b = Math.round(lerp(start[2], end[2], t))
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Get scale factor for bucket visualization
 */
export function getBucketScale(reach: number): number {
  const normalized = clampPercentage(reach) / 100
  if (normalized === 0) return 0
  return Math.max(normalized, 0.05)
}

/**
 * Find bucket for a given display percent
 */
function findBucketForDisplayPercent(
  scrollDepth: ScrollDepthSummary,
  displayPercent: number,
): ScrollDepthBucket | null {
  if (!scrollDepth || !scrollDepth.buckets?.length) {
    return null
  }

  const percentFromTop = clampPercentage(100 - displayPercent)
  const buckets = scrollDepth.buckets

  for (const bucket of buckets) {
    const start = clampPercentage(bucket.startPercent)
    const end = clampPercentage(bucket.endPercent)
    const isWithinRange =
      percentFromTop >= start &&
      (percentFromTop < end || (percentFromTop === 100 && end === 100))

    if (isWithinRange) {
      return bucket
    }
  }

  return buckets[buckets.length - 1] ?? null
}

/**
 * Get reach percentage for a scroll marker
 */
export function getMarkerReach(
  scrollDepth: ScrollDepthSummary,
  displayPercent: number,
): number | null {
  if (!scrollDepth || scrollDepth.totalSessions === 0) {
    return null
  }

  const bucket = findBucketForDisplayPercent(scrollDepth, displayPercent)
  if (!bucket) return null

  return toFixedNumber(bucket.reachPercentage, 1, 0)
}

/**
 * Build versions list from page data (history + current)
 */
export function buildVersionsList(page: any): ScreenshotVersion[] {
  const versions: ScreenshotVersion[] = []

  // Add historical versions
  if (page.screenshot_history && Array.isArray(page.screenshot_history)) {
    page.screenshot_history.forEach((historyEntry: any, index: number) => {
      versions.push({
        mobile_screenshot_url: historyEntry.mobile_screenshot_url || null,
        desktop_screenshot_url: historyEntry.desktop_screenshot_url || null,
        mobile_screenshot_at: historyEntry.mobile_screenshot_at || null,
        desktop_screenshot_at: historyEntry.desktop_screenshot_at || null,
        captured_at: historyEntry.captured_at,
        version_index: index,
      })
    })
  }

  // Add current version (if exists)
  if (page.mobile_screenshot_url || page.desktop_screenshot_url) {
    const capturedAt = page.mobile_screenshot_at || page.desktop_screenshot_at
    if (capturedAt) {
      versions.push({
        mobile_screenshot_url: page.mobile_screenshot_url || null,
        desktop_screenshot_url: page.desktop_screenshot_url || null,
        mobile_screenshot_at: page.mobile_screenshot_at || null,
        desktop_screenshot_at: page.desktop_screenshot_at || null,
        captured_at: capturedAt,
        version_index: versions.length,
      })
    }
  }

  // Sort by captured_at descending (newest first)
  versions.sort((a, b) => {
    return (
      new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime()
    )
  })

  // Reassign version indices after sorting
  versions.forEach((v, i) => {
    v.version_index = i
  })

  return versions
}

/**
 * Calculate date range for a version
 */
export function getDateRangeForVersion(
  versions: ScreenshotVersion[],
  versionIndex: number,
): { startDate: string; endDate: string } | null {
  if (!versions || versions.length === 0) return null

  const selectedVersion = versions[versionIndex]
  if (!selectedVersion) return null

  const startDate = selectedVersion.captured_at

  // Versions are sorted newest first (index 0 is newest)
  // For the newest version (index 0), endDate is current date
  // For older versions, endDate is when the previous (newer) version replaced it
  const endDate =
    versionIndex === 0
      ? new Date().toISOString()
      : versions[versionIndex - 1].captured_at

  return { startDate, endDate }
}

/**
 * Format a Date as YYYY-MM-DD in local time.
 * Do not use toISOString().split('T')[0] — local midnight / end-of-day
 * land on the previous or next UTC calendar day.
 */
export function formatLocalDateOnly(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format date to YYYY-MM-DD
 */
function formatDateOnly(dateString: string): string {
  return formatLocalDateOnly(new Date(dateString))
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never'

  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  return `${Math.floor(diffInSeconds / 86400)}d ago`
}

/**
 * Format full date for version display
 */
export function formatVersionDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format date range for display (e.g., "Jan 15 - Jan 22, 2025" or "Jan 15 - Present")
 */
export function formatDateRange(
  startDate: string,
  endDate: string,
  isPresent = false,
): string {
  const start = new Date(startDate)
  const startStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  if (isPresent) {
    return `${startStr} - Present`
  }

  const end = new Date(endDate)
  const endStr = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${startStr} - ${endStr}`
}

/** Parse YYYY-MM-DD as local start/end of day (matches external-api.ts). */
function parseLocalDateRange(
  startStr: string,
  endStr: string,
): { startDate: Date; endDate: Date } | null {
  const startDate = new Date(`${startStr}T00:00:00`)
  const endDate = new Date(`${endStr}T23:59:59.999`)
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null
  return { startDate, endDate }
}

/** Date-only APIs (ads since/until). Never use toISOString() — that is UTC. */
function toLocalDateRangeStrings(range: {
  startDate: Date
  endDate: Date
}): { since: string; until: string } {
  return {
    since: formatLocalDateOnly(range.startDate),
    until: formatLocalDateOnly(range.endDate),
  }
}

/**
 * Parse period to get date range.
 * Calendar-bound presets (MTD, WTD, QTD, YTD) anchor at local midnight so they
 * match traffic/analytics queries. Rolling presets use millisecond offsets.
 */
export function parsePeriodToDateRange(
  periodStr: string,
): { startDate: Date; endDate: Date } | null {
  // Colon-separated custom date range (YYYY-MM-DD:YYYY-MM-DD) from URL state
  if (/^\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/.test(periodStr)) {
    const [startStr, endStr] = periodStr.split(':')
    return parseLocalDateRange(startStr, endStr)
  }

  // Underscore-separated custom date range (YYYY-MM-DD_YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/.test(periodStr)) {
    const [startStr, endStr] = periodStr.split('_')
    return parseLocalDateRange(startStr, endStr)
  }

  const endDate = new Date()
  const MS_DAY = 24 * 60 * 60 * 1000

  switch (periodStr) {
    case 'TODAY':
      return {
        startDate: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()),
        endDate,
      }
    case 'YESTERDAY': {
      const y = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 1)
      return {
        startDate: y,
        endDate: new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59, 999),
      }
    }
    case '1d':
    case 'PAST_24_H':
      return { startDate: new Date(endDate.getTime() - MS_DAY), endDate }
    case '7d':
    case 'LAST_7_DAYS':
      return { startDate: new Date(endDate.getTime() - 7 * MS_DAY), endDate }
    case 'LAST_30_DAYS':
      return { startDate: new Date(endDate.getTime() - 30 * MS_DAY), endDate }
    case 'LAST_90_DAYS':
      return { startDate: new Date(endDate.getTime() - 90 * MS_DAY), endDate }
    case 'WEEK_TO_DATE':
      return {
        startDate: new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate() - endDate.getDay(),
        ),
        endDate,
      }
    case 'mtd':
    case 'MONTH_TO_DATE':
      return {
        startDate: new Date(endDate.getFullYear(), endDate.getMonth(), 1),
        endDate,
      }
    case 'QUARTER_TO_DATE':
      return {
        startDate: new Date(endDate.getFullYear(), Math.floor(endDate.getMonth() / 3) * 3, 1),
        endDate,
      }
    case 'YEAR_TO_DATE':
    case 'ytd':
      return {
        startDate: new Date(endDate.getFullYear(), 0, 1),
        endDate,
      }
    case 'LAST_12_MONTHS':
      return {
        startDate: new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate()),
        endDate,
      }
    case 'ALL_TIME':
      return {
        startDate: new Date(endDate.getFullYear() - 10, endDate.getMonth(), endDate.getDate()),
        endDate,
      }
    default:
      return { startDate: new Date(endDate.getTime() - 30 * MS_DAY), endDate }
  }
}

/** Parse a period into local YYYY-MM-DD bounds for date-only APIs. */
export function parsePeriodToLocalDateStrings(
  periodStr: string,
): { since: string; until: string } | null {
  const range = parsePeriodToDateRange(periodStr)
  if (!range) return null
  return toLocalDateRangeStrings(range)
}

/**
 * Check if heatmap date range overlaps with the date picker range
 */
export function isDateRangeWithinFilter(
  heatmapStartDate: string,
  heatmapEndDate: string,
  filterPeriod: string,
): boolean {
  const filterRange = parsePeriodToDateRange(filterPeriod)
  if (!filterRange) return true

  const heatmapStart = new Date(heatmapStartDate).getTime()
  const heatmapEnd = new Date(heatmapEndDate).getTime()
  const filterStart = new Date(filterRange.startDate).getTime()
  const filterEnd = new Date(filterRange.endDate).getTime()

  // 1-day buffer (86400000 ms) to handle slight misalignments
  const ONE_DAY_MS = 24 * 60 * 60 * 1000

  // Check if ranges overlap (with buffer)
  const hasOverlap =
    heatmapEnd + ONE_DAY_MS >= filterStart &&
    heatmapStart - ONE_DAY_MS <= filterEnd

  if (!hasOverlap) return false

  // Check if picker starts more than 1 day before heatmap start
  if (filterStart < heatmapStart - ONE_DAY_MS) return false

  // Check if picker ends more than 1 day after heatmap end
  const now = new Date().getTime()
  const isHeatmapOngoing = heatmapEnd >= now - ONE_DAY_MS
  if (!isHeatmapOngoing && filterEnd > heatmapEnd + ONE_DAY_MS) {
    return false
  }

  return true
}

/**
 * Ensure URL starts with leading slash
 */
export function ensureLeadingSlash(input: string): string {
  if (input === '') return ''
  return input.startsWith('/') ? input : `/${input}`
}

/**
 * Normalize heatmap API response
 */
export function normalizeHeatmapResponse(data: any): {
  points: any[]
  scrollDepth: ScrollDepthSummary
} {
  if (!data) {
    return { points: [], scrollDepth: null }
  }

  const rawPoints = Array.isArray(data) ? data : (data.points ?? data ?? [])
  const points: any[] = (rawPoints || []).map((point: any) => {
    const normalizedCount = toNumber(point.count ?? point.value, 0)
    const count = normalizedCount > 0 ? normalizedCount : 1
    const originalScreenSizeRaw =
      point.original_screen_size ?? point.originalScreenSize

    return {
      ...point,
      x: toNumber(point.x, 0),
      y: toNumber(point.y, 0),
      count,
      value: count,
      spread: toNumber(point.spread, 0),
      screen_size_range: point.screen_size_range ?? 'unknown',
      target: point.target ?? null,
      secondary: point.secondary ?? null,
      original_screen_size:
        originalScreenSizeRaw === undefined || originalScreenSizeRaw === null
          ? null
          : toNumber(originalScreenSizeRaw, 0),
    }
  })

  const rawScroll = Array.isArray(data)
    ? null
    : (data.scrollDepth ?? data.scroll_depth ?? null)

  if (!rawScroll) {
    return { points, scrollDepth: null }
  }

  const buckets: ScrollDepthBucket[] = (rawScroll.buckets ?? []).map(
    (bucket: any) => ({
      startPercent: toNumber(bucket.startPercent ?? bucket.start_percent, 0),
      endPercent: toNumber(bucket.endPercent ?? bucket.end_percent, 0),
      sessionsReached: toNumber(
        bucket.sessionsReached ?? bucket.sessions_reached,
        0,
      ),
      reachPercentage: toFixedNumber(
        bucket.reachPercentage ?? bucket.reach_percentage,
        2,
        0,
      ),
      dropOffPercentage: toFixedNumber(
        bucket.dropOffPercentage ?? bucket.drop_off_percentage,
        2,
        0,
      ),
    }),
  )

  const scrollDepth: ScrollDepthSummary = {
    totalSessions: toNumber(
      rawScroll.totalSessions ?? rawScroll.total_sessions,
      0,
    ),
    averageScroll: toFixedNumber(
      rawScroll.averageScroll ?? rawScroll.average_scroll,
      2,
      0,
    ),
    buckets,
  }

  return { points, scrollDepth }
}
